export interface Match {
  text: string;
  index: number;
  groups: string[];
  namedGroups?: Record<string, string>;
}

export interface RegexResult {
  success: boolean;
  matches?: Match[];
  error?: string;
}

export interface RegexTestOptions {
  pattern: string;
  flags: string;
  testString: string;
  global?: boolean;
}

/**
 * Test a regular expression against a string
 */
export function testRegex(options: RegexTestOptions): RegexResult {
  const { pattern, flags, testString, global = true } = options;

  try {
    const finalFlags = global ? `${flags}g` : flags;
    const regex = new RegExp(pattern, finalFlags);
    const matches: Match[] = [];

    if (global) {
      let match;
      while ((match = regex.exec(testString)) !== null) {
        matches.push({
          text: match[0],
          index: match.index,
          groups: match.slice(1),
          namedGroups: match.groups,
        });
        // Prevent infinite loops for zero-length matches
        if (!match[0].length) {
          regex.lastIndex++;
        }
      }
    } else {
      const match = regex.exec(testString);
      if (match) {
        matches.push({
          text: match[0],
          index: match.index,
          groups: match.slice(1),
          namedGroups: match.groups,
        });
      }
    }

    return { success: true, matches };
  } catch (e) {
    return { success: false, error: (e as Error).message };
  }
}

/**
 * Replace text using regex
 */
export function replaceWithRegex(
  testString: string,
  pattern: string,
  replacement: string,
  flags: string = 'g'
): RegexResult {
  try {
    const regex = new RegExp(pattern, flags);
    const result = testString.replace(regex, replacement);
    return { success: true, matches: [{ text: result, index: 0, groups: [] }] };
  } catch (e) {
    return { success: false, error: (e as Error).message };
  }
}

/* -------------------------------------------------------------------------- */
/* Common regex patterns (template library)                                    */
/* -------------------------------------------------------------------------- */

export interface CommonPattern {
  id: string;
  name: string;
  description: string;
  pattern: string;
  example: { input: string; matches: string[] };
}

export const commonPatterns: CommonPattern[] = [
  {
    id: 'email',
    name: 'Email',
    description: 'Match a standard email address (local@domain.tld).',
    pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}',
    example: {
      input: 'Contact support@example.com or sales@example.org for help.',
      matches: ['support@example.com', 'sales@example.org'],
    },
  },
  {
    id: 'url',
    name: 'URL',
    description: 'Match http/https URLs.',
    pattern: 'https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)',
    example: {
      input: 'Visit https://example.com or http://docs.example.org/page for info.',
      matches: ['https://example.com', 'http://docs.example.org/page'],
    },
  },
  {
    id: 'phone',
    name: 'Phone Number',
    description: 'International phone numbers with optional country code.',
    pattern: '\\+?\\d{1,4}?[-.\\s]?\\(?\\d{1,3}?\\)?[-.\\s]?\\d{1,4}[-.\\s]?\\d{1,4}[-.\\s]?\\d{1,9}',
    example: {
      input: 'Call +1 (555) 123-4567 or 415-555-9876',
      matches: ['+1 (555) 123-4567', '415-555-9876'],
    },
  },
  {
    id: 'ipv4',
    name: 'IPv4 Address',
    description: 'Match dotted-quad IPv4 addresses.',
    pattern: '\\b(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\b',
    example: {
      input: 'Servers: 192.168.1.1, 10.0.0.1, 255.255.255.0',
      matches: ['192.168.1.1', '10.0.0.1', '255.255.255.0'],
    },
  },
  {
    id: 'ipv6',
    name: 'IPv6 Address',
    description: 'Match full and shortened IPv6 addresses.',
    pattern: '(?:[A-Fa-f0-9]{1,4}:){7}[A-Fa-f0-9]{1,4}|::1|::',
    example: {
      input: 'Hosts: 2001:0db8:85a3:0000:0000:8a2e:0370:7334 and ::1',
      matches: ['2001:0db8:85a3:0000:0000:8a2e:0370:7334', '::1'],
    },
  },
  {
    id: 'dateISO',
    name: 'ISO Date',
    description: 'ISO 8601 date (YYYY-MM-DD).',
    pattern: '\\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\\d|3[01])',
    example: {
      input: 'Today is 2024-09-29 and tomorrow is 2024-09-30',
      matches: ['2024-09-29', '2024-09-30'],
    },
  },
  {
    id: 'time24h',
    name: '24-hour Time',
    description: 'Time in 24-hour HH:MM[:SS] format.',
    pattern: '\\b(?:[01]\\d|2[0-3]):[0-5]\\d(?::[0-5]\\d)?\\b',
    example: {
      input: 'Office hours: 09:00 to 17:30, lunch at 12:00:00',
      matches: ['09:00', '17:30', '12:00:00'],
    },
  },
  {
    id: 'hexColor',
    name: 'Hex Color',
    description: 'CSS-style hex color codes (#RGB or #RRGGBB).',
    pattern: '#(?:[0-9a-fA-F]{3}){1,2}\\b',
    example: {
      input: 'Theme uses #fff background, #3b82f6 primary, #1e40af accent',
      matches: ['#fff', '#3b82f6', '#1e40af'],
    },
  },
  {
    id: 'creditCard',
    name: 'Credit Card',
    description: 'Match common credit-card number formats.',
    pattern: '\\b(?:\\d{4}[- ]?){3}\\d{4}\\b',
    example: {
      input: 'Cards: 4111-1111-1111-1111 and 4111111111111111',
      matches: ['4111-1111-1111-1111', '4111111111111111'],
    },
  },
  {
    id: 'zipCode',
    name: 'US ZIP Code',
    description: 'US ZIP or ZIP+4.',
    pattern: '\\b\\d{5}(?:-\\d{4})?\\b',
    example: {
      input: 'Ship to 94103 or 94103-1234',
      matches: ['94103', '94103-1234'],
    },
  },
  {
    id: 'slug',
    name: 'URL Slug',
    description: 'Lowercase letters, digits, and dashes only.',
    pattern: '^[a-z0-9]+(?:-[a-z0-9]+)*$',
    example: {
      input: 'my-post-title\ninvalid_slug\nvalid-slug-123',
      matches: ['my-post-title', 'valid-slug-123'],
    },
  },
  {
    id: 'username',
    name: 'Username',
    description: '3–16 chars: letters, digits, underscores, dots.',
    pattern: '^[a-zA-Z0-9._]{3,16}$',
    example: {
      input: 'Valid: john_doe, jane.doe\nInvalid: a, this_username_is_way_too_long',
      matches: ['john_doe', 'jane.doe'],
    },
  },
  {
    id: 'hashtag',
    name: 'Hashtag',
    description: 'Social-media style hashtag.',
    pattern: '#[A-Za-z0-9_]+',
    example: {
      input: 'Trending: #javascript #webdev #devkit_2024',
      matches: ['#javascript', '#webdev', '#devkit_2024'],
    },
  },
  {
    id: 'mention',
    name: 'Mention',
    description: 'Social-media style @mention.',
    pattern: '@[A-Za-z0-9_]+',
    example: {
      input: 'Thanks @alice and @bob_smith for the review!',
      matches: ['@alice', '@bob_smith'],
    },
  },
  {
    id: 'strongPassword',
    name: 'Strong Password',
    description: '8+ chars with upper, lower, digit, and symbol.',
    pattern: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$',
    example: {
      input: 'Good: P@ssw0rd123\nBad: password123',
      matches: ['P@ssw0rd123'],
    },
  },
  {
    id: 'macAddress',
    name: 'MAC Address',
    description: 'MAC address in colon or hyphen form.',
    pattern: '(?:[0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}',
    example: {
      input: 'MACs: 00:1A:2B:3C:4D:5E and 00-1A-2B-3C-4D-5E',
      matches: ['00:1A:2B:3C:4D:5E', '00-1A-2B-3C-4D-5E'],
    },
  },
];

/**
 * Look up a common pattern by id.
 */
export function getPatternById(id: string): CommonPattern | undefined {
  return commonPatterns.find((p) => p.id === id);
}

/* -------------------------------------------------------------------------- */
/* Regex Builder — compose patterns from natural-language parts                 */
/* -------------------------------------------------------------------------- */

export interface RegexBuilderRequest {
  /** Capture digits only. */
  digits?: boolean;
  /** Capture letters (lower/upper). */
  letters?: boolean;
  /** Required length (exact). */
  length?: number;
  /** Min/max length range. */
  minLength?: number;
  maxLength?: number;
  /** Optional literal prefix. */
  prefix?: string;
  /** Optional literal suffix. */
  suffix?: string;
  /** Allow whitespace inside. */
  whitespace?: boolean;
  /** Allow dashes. */
  dashes?: boolean;
  /** Allow underscores. */
  underscores?: boolean;
}

export interface RegexBuilderResult {
  success: boolean;
  data?: string;
  error?: string;
}

/**
 * Build a regex pattern from a structured request.
 * Useful for generating patterns from form-style inputs.
 */
export function buildRegexFromParts(req: RegexBuilderRequest): RegexBuilderResult {
  const {
    digits = false,
    letters = false,
    length,
    minLength,
    maxLength,
    prefix = '',
    suffix = '',
    whitespace = false,
    dashes = false,
    underscores = false,
  } = req;

  if (!digits && !letters && !whitespace && !dashes && !underscores) {
    return { success: false, error: 'Select at least one allowed character type.' };
  }

  let charClass = '';
  if (letters) charClass += 'a-zA-Z';
  if (digits) charClass += '0-9';
  if (whitespace) charClass += '\\s';
  if (dashes) charClass += '\\-';
  if (underscores) charClass += '_';

  let quantifier: string;
  if (typeof length === 'number' && length > 0) {
    quantifier = `{${length}}`;
  } else if (typeof minLength === 'number' && typeof maxLength === 'number' && maxLength >= minLength) {
    quantifier = `{${minLength},${maxLength}}`;
  } else if (typeof minLength === 'number' && minLength > 0) {
    quantifier = `{${minLength},}`;
  } else if (typeof maxLength === 'number' && maxLength > 0) {
    quantifier = `{0,${maxLength}}`;
  } else {
    quantifier = '+';
  }

  // Escape any literal prefix/suffix.
  const escapeLiteral = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const escapedPrefix = prefix ? escapeLiteral(prefix) : '';
  const escapedSuffix = suffix ? escapeLiteral(suffix) : '';

  const pattern = `${escapedPrefix}[${charClass}]${quantifier}${escapedSuffix}`;
  return { success: true, data: pattern };
}

/* -------------------------------------------------------------------------- */
/* Regex Generator — return a pattern + sample for a named use case            */
/* -------------------------------------------------------------------------- */

export interface GeneratedRegex {
  pattern: string;
  description: string;
  sampleInput: string;
  sampleMatch: string;
}

/**
 * Generate a regex for a known, named use case (e.g. "email", "ipv4").
 * Returns undefined if the id is unknown.
 */
export function generateRegexFor(useCaseId: string): GeneratedRegex | undefined {
  const p = getPatternById(useCaseId);
  if (!p) return undefined;
  return {
    pattern: p.pattern,
    description: p.description,
    sampleInput: p.example.input,
    sampleMatch: p.example.matches[0] ?? '',
  };
}
